import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search as SearchIcon, MapPin, SlidersHorizontal, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AFRICAN_COUNTRIES } from "@/lib/countries";

const CATEGORIES = [
    { v: "all", l: "Toutes" },
    { v: "fashion", l: "Mode" },
    { v: "beauty", l: "Beauté" },
    { v: "food", l: "Nourriture" },
    { v: "electronics", l: "Électronique" },
    { v: "home", l: "Maison" },
    { v: "services", l: "Services" },
    { v: "cars", l: "Auto" },
    { v: "other", l: "Autre" },
];

export default function SearchPage() {
    const [q, setQ] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [category, setCategory] = useState("all");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [availableCities, setAvailableCities] = useState([]);
    const [availableCountries, setAvailableCountries] = useState([]);

    useEffect(() => {
        api.get("/products/filters")
            .then((r) => {
                setAvailableCities(r.data.cities || []);
                setAvailableCountries(r.data.countries || []);
            })
            .catch((err) => console.debug("filters fetch failed", err?.response?.status));
    }, []);

    useEffect(() => {
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const params = {};
                if (q) params.q = q;
                if (city) params.city = city;
                if (country) params.country = country;
                if (category && category !== "all") params.category = category;
                if (minPrice) params.min_price = minPrice;
                if (maxPrice) params.max_price = maxPrice;
                const { data } = await api.get("/products", { params });
                setProducts(data);
            } catch (err) {
                console.debug("search failed", err?.response?.status);
                setProducts([]);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [q, city, country, category, minPrice, maxPrice]);

    const clearAll = () => {
        setQ("");
        setCity("");
        setCountry("");
        setCategory("all");
        setMinPrice("");
        setMaxPrice("");
    };

    const activeFilters = [city, country, category !== "all" && category, minPrice, maxPrice].filter(Boolean).length;

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-32 grain-bg">
            <div className="max-w-lg mx-auto px-4 pt-6">
                <h1 className="font-display font-black text-3xl mb-4">Explorer</h1>

                <div className="relative mb-3">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                        data-testid="search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Titre, description, boutique, ville, pays..."
                        className="pl-12 pr-16 bg-white/5 border-white/10 h-13 text-white placeholder:text-white/40 rounded-full"
                    />
                    <button
                        onClick={() => setShowFilters((s) => !s)}
                        data-testid="toggle-filters"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-3 rounded-full bg-white/10 border border-white/20 flex items-center gap-1 font-body text-xs"
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        {activeFilters > 0 && <span className="bg-[#FF5722] w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold">{activeFilters}</span>}
                    </button>
                </div>

                {/* Category quick chips */}
                <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-4 px-4" style={{ scrollbarWidth: "none" }}>
                    {CATEGORIES.map((c) => (
                        <Badge
                            key={c.v}
                            onClick={() => setCategory(c.v)}
                            className={`cursor-pointer whitespace-nowrap px-4 py-2 rounded-full font-body ${category === c.v ? "bg-[#FF5722] text-white" : "bg-white/10 text-white/80"}`}
                            data-testid={`filter-cat-${c.v}`}
                        >
                            {c.l}
                        </Badge>
                    ))}
                </div>

                {/* Advanced filters */}
                {showFilters && (
                    <div className="glass rounded-2xl p-4 mb-4 space-y-3 fade-up" data-testid="filters-panel">
                        <div className="flex items-center justify-between">
                            <p className="font-display font-bold text-sm">Filtres avancés</p>
                            {activeFilters > 0 && (
                                <button onClick={clearAll} data-testid="clear-filters" className="text-[11px] text-[#FF5722] font-body flex items-center gap-1">
                                    <X className="w-3 h-3" /> Réinitialiser
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[11px] uppercase tracking-wide text-white/60 font-body">Pays</label>
                                <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
                                    <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-10 text-white text-sm" data-testid="filter-country">
                                        <SelectValue placeholder="Tous" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        <SelectItem value="all">Tous les pays</SelectItem>
                                        {(availableCountries.length ? availableCountries : AFRICAN_COUNTRIES).map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wide text-white/60 font-body">Ville</label>
                                <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
                                    <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-10 text-white text-sm" data-testid="filter-city">
                                        <SelectValue placeholder="Toutes" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        <SelectItem value="all">Toutes les villes</SelectItem>
                                        {availableCities.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[11px] uppercase tracking-wide text-white/60 font-body">Prix min (FCFA)</label>
                                <Input
                                    type="number"
                                    data-testid="filter-min-price"
                                    value={minPrice}
                                    onChange={(e) => setMinPrice(e.target.value)}
                                    placeholder="0"
                                    className="mt-1 bg-white/5 border-white/10 h-10 text-white placeholder:text-white/30 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wide text-white/60 font-body">Prix max (FCFA)</label>
                                <Input
                                    type="number"
                                    data-testid="filter-max-price"
                                    value={maxPrice}
                                    onChange={(e) => setMaxPrice(e.target.value)}
                                    placeholder="∞"
                                    className="mt-1 bg-white/5 border-white/10 h-10 text-white placeholder:text-white/30 text-sm"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Results count */}
                <p className="text-xs text-white/50 font-body mb-3" data-testid="results-count">
                    {loading ? "Recherche..." : `${products.length} résultat${products.length > 1 ? "s" : ""}`}
                </p>

                {loading ? (
                    <div className="grid grid-cols-2 gap-3">
                        {["s1","s2","s3","s4","s5","s6"].map((k) => <div key={k} className="skeleton aspect-[3/4]" />)}
                    </div>
                ) : products.length === 0 ? (
                    <div className="glass rounded-2xl p-8 text-center">
                        <p className="text-white/60 font-body">Aucun résultat pour ces critères</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((p) => (
                            <Link
                                to={`/watch/${p.id}`}
                                key={p.id}
                                data-testid={`search-result-${p.id}`}
                                className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-white/5 group"
                            >
                                <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent p-3 flex flex-col justify-end">
                                    <p className="font-display font-bold text-sm text-white line-clamp-1">{p.title}</p>
                                    <p className="text-[11px] text-white/80 font-body flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {[p.city, p.country].filter(Boolean).join(", ")}
                                    </p>
                                    <p className="font-display font-black text-base text-[#FFC107] mt-1">
                                        {Number(p.price).toLocaleString("fr-FR")} FCFA
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
            <BottomNav />
        </div>
    );
}
